/**
 * hoursApi.js
 * -----------
 * Real REST API client for Education > Lectures > Lecture Hours.
 * Connects directly to FastAPI backend /api/lectures/hours adhering strictly
 * to module_implementation_standards.md (Rule 90: Zero Mock Data).
 */
import api from '../../../../api/client'
import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'

export const HOURS_SEARCH_FIELDS = [
  { value: 'All', label: 'All Fields' },
  { value: 'Faculty', label: 'Faculty' },
  { value: 'Course', label: 'Course' },
  { value: 'Subject', label: 'Subject' },
  { value: 'Topic', label: 'Topic / Chapter' },
]

export const HOURS_PREDEFINED_REPORTS = [
  {
    reportKey: 'lecture_hours_summary',
    reportId: 5300,
    title: 'Lecture Hours & Faculty Teaching Ledger',
    description: 'Reconciles delivered lecture duration, timeline, and teaching hours across faculties and courses',
    defaultColumns: ['aDate', 'CourseName', 'BatchNames', 'BranchNames', 'SubjectName', 'ChapterNo', 'ChapterName', 'aFrom', 'aTill', 'TimeMin', 'TimeDiff', 'FacultyName'],
  },
]

/**
 * Legacy OrderBy numeric mapping:
 * 1: InstituteName, 2: BranchNames, 3: CourseName, 4: BatchNames, 5: SubjectName,
 * 6: ChapterNo, 7: Date/Time (default), 8: FacultyName, 9: SchoolName, 10: Hours
 */
export const ORDER_BY_MAP = {
  company: 1,
  branch: 2,
  course: 3,
  batch: 4,
  subject: 5,
  chapter: 6,
  date: 7,
  faculty: 8,
  school: 9,
  hours: 10,
  timeMin: 10,
}

/**
 * Fetch paginated lecture hours ledger records from backend.
 */
export async function fetchLectureHours(params = {}) {
  const query = {}

  if (params.academicYear && params.academicYear !== 'All') {
    query.academicYear = params.academicYear
  }

  if (params.companyFilter && params.companyFilter !== 'All') {
    const id = Number(params.companyFilter)
    if (!isNaN(id)) query.instituteId = id
  }
  if (params.branchFilter && params.branchFilter !== 'All') {
    const id = Number(params.branchFilter)
    if (!isNaN(id)) query.branchId = id
  }
  if (params.courseFilter && params.courseFilter !== 'All') {
    const id = Number(params.courseFilter)
    if (!isNaN(id)) query.courseId = id
  }
  if (params.batchFilter && params.batchFilter !== 'All') {
    const id = Number(params.batchFilter)
    if (!isNaN(id)) query.batchId = id
  }
  if (params.subjectFilter && params.subjectFilter !== 'All') {
    const id = Number(params.subjectFilter)
    if (!isNaN(id)) query.subjectId = id
  }
  if (params.facultyFilter && params.facultyFilter !== 'All') {
    const id = Number(params.facultyFilter)
    if (!isNaN(id)) query.facultyId = id
  }
  if (params.schoolFilter && params.schoolFilter !== 'All') {
    const id = Number(params.schoolFilter)
    if (!isNaN(id)) query.schoolId = id
  }

  if (params.fromDate) {
    query.fromDate = params.fromDate
  }
  if (params.toDate) {
    query.toDate = params.toDate
  }
  if (params.includeExtra !== undefined) {
    query.includeExtra = Boolean(params.includeExtra)
  }
  if (params.searchField) {
    query.searchField = params.searchField
  }
  if (params.searchText && params.searchText.trim()) {
    query.searchText = params.searchText.trim()
  }

  if (params.orderBy !== undefined) {
    query.orderBy = typeof params.orderBy === 'number'
      ? params.orderBy
      : ORDER_BY_MAP[params.orderBy] || 7
  } else {
    query.orderBy = 7
  }

  if (params.page) {
    query.page = params.page
  }
  if (params.pageSize) {
    query.pageSize = params.pageSize
  }

  const { data } = await api.get('/lectures/hours', { params: query })

  // Transform keys for table consumption if needed
  const records = (data.items || []).map((item) => ({
    id: item.id,
    company: item.institute_name,
    branch: item.branch_names,
    course: item.course_name,
    batch: item.batch_names,
    subject: item.subject_name,
    chapterNo: item.chapter_no,
    chapterName: item.chapter_name,
    faculty: item.faculty_name,
    facultyAvatar: (item.faculty_name || 'FC').slice(0, 2).toUpperCase(),
    schoolCollege: item.school_name,
    date: item.a_date,
    startTime: item.a_from,
    endTime: item.a_till,
    timeMin: item.time_min,
    timeDiff: item.time_diff,
    extraLecture: item.extra_lecture,
  }))

  return {
    records,
    totalCount: data.total,
    totalPages: data.total_pages,
    currentPage: data.page,
    pageSize: data.page_size,
  }
}

/**
 * Fetch summary KPI metrics for lecture hours ledger.
 */
export async function fetchLectureHoursMetrics(params = {}) {
  const query = {}

  if (params.academicYear && params.academicYear !== 'All') {
    query.academicYear = params.academicYear
  }
  if (params.companyFilter && params.companyFilter !== 'All') {
    const id = Number(params.companyFilter)
    if (!isNaN(id)) query.instituteId = id
  }
  if (params.branchFilter && params.branchFilter !== 'All') {
    const id = Number(params.branchFilter)
    if (!isNaN(id)) query.branchId = id
  }
  if (params.courseFilter && params.courseFilter !== 'All') {
    const id = Number(params.courseFilter)
    if (!isNaN(id)) query.courseId = id
  }
  if (params.batchFilter && params.batchFilter !== 'All') {
    const id = Number(params.batchFilter)
    if (!isNaN(id)) query.batchId = id
  }
  if (params.subjectFilter && params.subjectFilter !== 'All') {
    const id = Number(params.subjectFilter)
    if (!isNaN(id)) query.subjectId = id
  }
  if (params.facultyFilter && params.facultyFilter !== 'All') {
    const id = Number(params.facultyFilter)
    if (!isNaN(id)) query.facultyId = id
  }
  if (params.schoolFilter && params.schoolFilter !== 'All') {
    const id = Number(params.schoolFilter)
    if (!isNaN(id)) query.schoolId = id
  }
  if (params.fromDate) {
    query.fromDate = params.fromDate
  }
  if (params.toDate) {
    query.toDate = params.toDate
  }
  if (params.includeExtra !== undefined) {
    query.includeExtra = Boolean(params.includeExtra)
  }
  if (params.searchField) {
    query.searchField = params.searchField
  }
  if (params.searchText && params.searchText.trim()) {
    query.searchText = params.searchText.trim()
  }

  const { data } = await api.get('/lectures/hours/metrics', { params: query })

  return {
    totalRecords: data.total_lectures || 0,
    totalMinutes: data.total_minutes || 0,
    totalHoursString: data.total_hours_formatted || '0h 0m',
    hoursFormattedSummary: `${data.total_lectures || 0} lectures (${data.total_hours_formatted || '0h 0m'})`,
    uniqueFacultyCount: data.active_faculties_count || 0,
    uniqueCourseCount: data.courses_count || 0,
    avgDurationMinutes:
      data.total_lectures > 0 ? Math.round(data.total_minutes / data.total_lectures) : 0,
  }
}

/**
 * Dynamically fetch real tenant-scoped options for filters (Rule 90).
 */
export async function fetchLectureHoursMasterOptions() {
  try {
    const [cItems, bItems, sItems, brItems, compItems, scItems] = await Promise.all([
      getMasterOptionItems('courses'),
      getMasterOptionItems('batches'),
      getMasterOptionItems('subjects'),
      getMasterOptionItems('branches'),
      getMasterOptionItems('institutes'),
      getMasterOptionItems('schools'),
    ])

    let facItems = []
    try {
      const { data: facData } = await api.get('/options/acheads', { params: { page: 1, page_size: 100 } })
      facItems = facData?.items || []
    } catch (err) {
      console.error('Error loading faculties from /options/acheads:', err)
    }

    return {
      courses: Array.isArray(cItems) ? cItems.map((c) => ({ value: String(c.id), label: c.name })) : [],
      batches: Array.isArray(bItems) ? bItems.map((b) => ({ value: String(b.id), label: b.name })) : [],
      subjects: Array.isArray(sItems) ? sItems.map((s) => ({ value: String(s.id), label: s.name })) : [],
      branches: Array.isArray(brItems) ? brItems.map((b) => ({ value: String(b.id), label: b.name })) : [],
      companies: Array.isArray(compItems) ? compItems.map((c) => ({ value: String(c.id), label: c.name })) : [],
      schools: Array.isArray(scItems) ? scItems.map((s) => ({ value: String(s.id), label: s.name })) : [],
      faculty: Array.isArray(facItems) ? facItems.map((f) => ({ value: String(f.id || f.value), label: f.name || f.label })) : [],
    }
  } catch (err) {
    console.error('Error in fetchLectureHoursMasterOptions:', err)
    return {
      courses: [],
      batches: [],
      subjects: [],
      branches: [],
      companies: [],
      schools: [],
      faculty: [],
    }
  }
}
